import { Progress } from "@/components/ui/progress";
import { type DashboardTrack, useAdminUpdateTrack } from "@/lib/api";
import { logger } from "@/lib/logger";
import {
  boardReadinessBadgeClass,
  boardReadinessLabel,
  phaseLabel,
  prototypeDotClass,
  stageBadgeClass,
  valueConfidenceClass,
  valueConfidenceLabel,
} from "@/lib/phase";
import { cn } from "@/lib/utils";

import {
  BOARD_READINESS_OPTIONS,
  InlineSelect,
  InlineText,
  isProgramTrack,
  NOT_SET,
  PROTOTYPE_RAG_OPTIONS,
  resolveNullable,
  STAGE_OPTIONS,
  trackScalarVars,
  VALUE_CONFIDENCE_OPTIONS,
} from "./inline";

interface UseCasePipelineProps {
  tracks: DashboardTrack[];
  editMode: boolean;
}

const HEADERS = [
  "BU / Champion",
  "Selected use case",
  "Stage",
  "Use cases identified",
  "Prioritized",
  "Prototype",
  "Value confidence",
  "Board readiness",
];

/** Parse a numeric input string into a number | null (empty → null). */
function toNumberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function UseCasePipeline({ tracks, editMode }: UseCasePipelineProps) {
  // The pipeline is per-champion use cases — the program lane has none, so
  // exclude program tracks entirely.
  const championTracks = tracks.filter((t) => !isProgramTrack(t));
  const maxIdentified = Math.max(1, ...championTracks.map((t) => t.use_cases_identified ?? 0));
  const updateTrack = useAdminUpdateTrack();

  /** Apply a single scalar change on top of the full current scalar set. */
  const patch = (track: DashboardTrack, change: Partial<ReturnType<typeof trackScalarVars>>) => {
    logger.debug("[dashboard]", "pipeline edit", track.id, change);
    updateTrack.mutate({ ...trackScalarVars(track), ...change });
  };

  return (
    <div className="space-y-3">
      <div className="border-border overflow-x-auto rounded-md border">
        <table className="w-full min-w-[880px] border-collapse">
          <thead>
            <tr className="bg-boi-dark">
              {HEADERS.map((h) => (
                <th key={h} className="p-3 text-left text-[10px] font-medium text-white uppercase">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {championTracks.map((track) => {
              const identified = track.use_cases_identified;
              const pct = identified != null ? Math.round((identified / maxIdentified) * 100) : 0;
              return (
                <tr key={track.id} className="border-border border-b last:border-b-0">
                  <td className="p-3 align-middle">
                    <p className="text-foreground text-sm font-medium">{track.name}</p>
                    {editMode ? (
                      <InlineText
                        value={track.champion_note ?? ""}
                        placeholder="Champion note"
                        className="mt-1"
                        onSave={(v) => patch(track, { championNote: v.trim() || null })}
                      />
                    ) : (
                      track.champion_note && (
                        <p className="text-muted-foreground text-xs">{track.champion_note}</p>
                      )
                    )}
                  </td>
                  <td className="p-3 align-middle">
                    {editMode ? (
                      <InlineText
                        value={track.selected_use_case ?? ""}
                        placeholder="Selected use case"
                        onSave={(v) => patch(track, { selectedUseCase: v.trim() || null })}
                      />
                    ) : track.selected_use_case ? (
                      <p className="text-foreground text-sm font-medium">
                        {track.selected_use_case}
                      </p>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </td>
                  <td className="p-3 align-middle">
                    {editMode ? (
                      <InlineSelect
                        value={track.stage ?? "not_started"}
                        options={STAGE_OPTIONS}
                        onSave={(v) => patch(track, { stage: v })}
                      />
                    ) : (
                      <span className={stageBadgeClass(track.stage)}>
                        {phaseLabel(track.stage)}
                      </span>
                    )}
                  </td>
                  <td className="p-3 align-middle">
                    {editMode ? (
                      <InlineText
                        type="number"
                        value={identified != null ? String(identified) : ""}
                        placeholder="0"
                        className="w-20"
                        onSave={(v) => patch(track, { useCasesIdentified: toNumberOrNull(v) })}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Progress value={pct} className="h-1.5 w-24" />
                        <span className="text-foreground w-8 text-right text-xs font-medium tabular-nums">
                          {identified ?? "—"}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="p-3 align-middle">
                    {editMode ? (
                      <InlineText
                        type="number"
                        value={
                          track.use_cases_prioritized != null
                            ? String(track.use_cases_prioritized)
                            : ""
                        }
                        placeholder="0"
                        className="w-20"
                        onSave={(v) => patch(track, { useCasesPrioritized: toNumberOrNull(v) })}
                      />
                    ) : (
                      <span className="text-muted-foreground text-sm tabular-nums">
                        {track.use_cases_prioritized ?? "—"}
                      </span>
                    )}
                  </td>
                  <td className="p-3 align-middle">
                    {editMode ? (
                      <div className="space-y-1">
                        <InlineSelect
                          value={track.prototype_rag ?? NOT_SET}
                          options={PROTOTYPE_RAG_OPTIONS}
                          onSave={(v) => patch(track, { prototypeRag: resolveNullable(v) })}
                        />
                        <InlineText
                          value={track.prototype_note ?? ""}
                          placeholder="Prototype note"
                          onSave={(v) => patch(track, { prototypeNote: v.trim() || null })}
                        />
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-xs">
                        <span
                          className={cn(
                            "h-2 w-2 shrink-0 rounded-full",
                            prototypeDotClass(track.prototype_rag),
                          )}
                          aria-hidden
                        />
                        <span className="text-muted-foreground">{track.prototype_note ?? "—"}</span>
                      </span>
                    )}
                  </td>
                  <td className="p-3 align-middle">
                    {editMode ? (
                      <InlineSelect
                        value={track.value_confidence ?? NOT_SET}
                        options={VALUE_CONFIDENCE_OPTIONS}
                        onSave={(v) => patch(track, { valueConfidence: resolveNullable(v) })}
                      />
                    ) : (
                      <span className={cn("text-xs", valueConfidenceClass(track.value_confidence))}>
                        {valueConfidenceLabel(track.value_confidence)}
                      </span>
                    )}
                  </td>
                  <td className="p-3 align-middle">
                    {editMode ? (
                      <InlineSelect
                        value={track.board_readiness ?? NOT_SET}
                        options={BOARD_READINESS_OPTIONS}
                        onSave={(v) => patch(track, { boardReadiness: resolveNullable(v) })}
                      />
                    ) : (
                      <span className={boardReadinessBadgeClass(track.board_readiness)}>
                        {boardReadinessLabel(track.board_readiness)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground text-xs">
        ⚡ AI-generated from Granola meeting notes · Review and approve before sharing
      </p>
    </div>
  );
}
